import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { dehydrate, QueryClient } from "react-query";

import { env } from "@monta/env";
import createApi from "./api";
import { checkRegister, loginErrorHandler, redirect } from "./auth-handlers";
import {
  ALREADY_LOGGEDIN,
  CHANGE_PASSWORD,
  CHANGE_USER,
  GET_USER,
  GET_USERS,
  LOGIN,
  LOGOUT
} from "./endpoints";
import { FeaturesEnum, UsersTypeEnum } from "./enums";
import { Features } from "./features";
import { ServerLogger } from "./logger";
import { generateTabId } from "./tab-id";

import type { TUtils } from ".";
import type { NextPageContext } from "next/types";
import type {
  IAxiosInstance,
  TGetUsersResponse,
  TJWT,
  TLoginResponse,
  TSelectUserResponse,
  TUser,
  TWorkerMessagePayload
} from "./types";

export const getDataFromQueryClient = <T>(
  client: QueryClient,
  key: string
): T => {
  return dehydrate(client).queries.find((i) => i.queryKey === key)?.state
    .data as T;
};

export const AUTH_UPDATE_EVENT = "AUTH_UPDATE_EVENT";

const queryClient = new QueryClient();
queryClient.prefetchQuery("get-user", () => Promise.resolve(null));

export class Auth {
  ctx?: NextPageContext;
  _user?: TUser;
  api!: IAxiosInstance;
  storage!: TUtils["storage"];
  channel?: BroadcastChannel;
  token?: string;
  refreshToken?: string;
  refreshing?: Promise<unknown>;
  initializing?: Promise<unknown>;

  constructor(
    storage: TUtils["storage"],
    ctx?: NextPageContext,
    initialUser?: TUser
  ) {
    this._user = initialUser;
    if (typeof window === "undefined" && !ctx) return;
    this.storage = storage;
    this.ctx = ctx;
    this.token = this.storage?.getCookie<string>("accessToken");
    this.refreshToken = this.storage?.getCookie<string>("refreshToken");
    ServerLogger.info(`${ctx?.req?.url} --- ${this.token}`, "request");

    this.api = createApi({
      ctx,
      auth: this
    });
    if (!ctx || ctx.pathname) this.initializing = this.init();
    if (typeof window !== "undefined") {
      this.initSharedWorker();
    }
  }

  initSharedWorker() {
    globalThis.auth = this;
    this.channel = new BroadcastChannel("monta-auth-channel");
    this.channel.onmessage = (e: MessageEvent<TWorkerMessagePayload>) => {
      const { data, type } = e.data;
      switch (type) {
        case "REFRESH":
        case "LOGIN":
          this.token = data.accessToken;
          this.refreshToken = data.refreshToken;
          this.getUser();
          break;
        case "LOGOUT":
          this.logout(false);
          break;
      }
    };
    this.channel.onmessageerror = (e) => console.error(e);
  }

  async init() {
    if (this.token) {
      ServerLogger.log(String(this.ctx?.asPath), "path", "magenta");
      ServerLogger.info(`token: ${this.token}`);
      try {
        ServerLogger.info(`checking for token expiration: ${this.token}`);
        if (this.isExpired()) {
          ServerLogger.info(`expired: ${this.token}`);
          // if ((await this.refresh()) !== "logout")
          //   ServerLogger.success(`refreshed: ${this.token}`);
          // else {
          //   ServerLogger.warning(`failed to refresh token for: ${this.token}`);
          //   return;
          // }
        } else {
          ServerLogger.info(`token not expired: ${this.token}`);
        }
        ServerLogger.info(`getting user for: ${this.token}`);
        await this.getUser();
        ServerLogger.info(`found user for: ${this.token}`);
      } catch (e) {
        ServerLogger.error(String(e));
        this.logout();
      }
    }
  }

  useAuth() {
    const [auth, setAuth] = useState(this._user);

    useEffect(() => {
      const cb = () => {
        setAuth(this._user);
      };
      window.addEventListener(AUTH_UPDATE_EVENT, cb);
      cb();
      return () => {
        window.removeEventListener(AUTH_UPDATE_EVENT, cb);
      };
    }, []);

    return auth || this._user;
  }

  private dispatchUserUpdate() {
    if (typeof window !== "undefined")
      window.dispatchEvent(new Event(AUTH_UPDATE_EVENT));
  }

  private async getUser() {
    const getData = () =>
      getDataFromQueryClient<TUser>(queryClient, this.token || "get-user");

    const data = getData();

    if (data) {
      ServerLogger.warning(`return user object from cahce for: ${this.token}`);
      this._user = data;
      return Promise.resolve(data);
    }

    await queryClient.prefetchQuery(
      this.token || "get-user",
      () =>
        this.api
          .$get<TUser>(GET_USER())
          .then((res) => {
            this._user = res;
            this.dispatchUserUpdate();
            checkRegister(this, res.registrationStateId, this.ctx);
            return res;
          })
          .catch(loginErrorHandler),
      {
        staleTime: 1 * 24 * 60 * 60 * 1000
      }
    );
    return getData();
  }

  callAlreadyLogin = async (api: IAxiosInstance): Promise<TUser | null> => {
    try {
      return await api.$post<TUser>(ALREADY_LOGGEDIN(), { fp: "" });
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  async login(username: string, password: string) {
    try {
      const res = await this.api.$post<TLoginResponse>(LOGIN(), {
        username,
        password
      });
      generateTabId();
      this.saveTokens(res);
      await this.getUser();
      this.channel?.postMessage({ type: "LOGIN", data: res });
      await this.callAlreadyLogin(this.api).then((userData) => {
        localStorage.setItem("user", JSON.stringify(userData));
        window.dispatchEvent(new Event("LOCAL_STORAGE_USER_UPDATE_EVENT"));
      });
      if (
        !(await Features.get(this.api, FeaturesEnum.CHANGE_ACCOUNT_AFTER_LOGIN))
      ) {
        Features.set(this.api, FeaturesEnum.CHANGE_ACCOUNT_AFTER_LOGIN).then(
          () => {
            const params = new URLSearchParams(window.location.search);
            params.set("first-login", "true");
            redirect(
              "/profile/account-list?" + params.toString(),
              "APPLICATION_URL",
              this.ctx
            );
          }
        );
      }
      return this._user;
    } catch (e: any) {
      return loginErrorHandler(e);
    }
  }

  async loginWithToken(tokens: TLoginResponse) {
    try {
      generateTabId();
      this.saveTokens(tokens);
      await this.getUser();
      this.channel?.postMessage({ type: "LOGIN", data: tokens });
      await this.callAlreadyLogin(this.api).then((userData) => {
        localStorage.setItem("user", JSON.stringify(userData));
        globalThis.window.dispatchEvent(
          new Event("LOCAL_STORAGE_USER_UPDATE_EVENT")
        );
      });
      if (
        !(await Features.get(this.api, FeaturesEnum.CHANGE_ACCOUNT_AFTER_LOGIN))
      ) {
        Features.set(this.api, FeaturesEnum.CHANGE_ACCOUNT_AFTER_LOGIN).then(
          () => {
            const params = new URLSearchParams(
              globalThis.window.location.search
            );
            params.set("first-login", "true");
            redirect(
              "/profile/account-list?" + params.toString(),
              "APPLICATION_URL",
              this.ctx
            );
          }
        );
      }
      return this._user;
    } catch (e: any) {
      return loginErrorHandler(e);
    }
  }

  async loginByOTP(otpCode: string, phoneNumber: string) {
    const data = {
      code: otpCode,
      phoneNumber: phoneNumber
    };
    try {
      const res = await this.api.$post<{
        refreshToken: string;
        accessToken: string;
      }>("/auth/login-by-otp", data);
      this.saveTokens(res);
      this.channel?.postMessage({ type: "LOGIN", data: res });
      return this._user;
    } catch (error: any) {
      return loginErrorHandler(error);
    }
  }

  logout(updateWorker = true, options?: { shouldRedirect?: boolean }) {
    const { shouldRedirect = true } = options || {};
    return this.api
      .$post(LOGOUT())
      .then(() => {
        this.storage.removeCookie("accessToken");
        this.storage.removeCookie("refreshToken");
        this.token = undefined;
        this.refreshToken = undefined;
        this._user = undefined;
        this.dispatchUserUpdate();
        if (updateWorker) this.channel?.postMessage({ type: "LOGOUT" });
        if (shouldRedirect) redirect("/", "LANDING_URL", this.ctx);
      })
      .catch((error) => {
        console.error("Auth logout error", error);
        ServerLogger.error(error);
      });
  }

  changePassword(password) {
    return this.api.$post(CHANGE_PASSWORD(), {
      password
    });
  }

  saveTokens(res: { accessToken: string; refreshToken: string }) {
    ServerLogger.info(env.COOKIE_DOMAIN, "domain");

    this.storage?.setCookie(
      ["accessToken", "refreshToken"],
      [res.accessToken, res.refreshToken],
      {
        domain: env.COOKIE_DOMAIN,
        path: "/",
        expires: dayjs().add(2, "weeks").toDate(),
        secure: true,
        sameSite: "None"
      }
    );

    this.token = res.accessToken;
    this.refreshToken = res.refreshToken;
  }

  getUsers(): Promise<TGetUsersResponse> {
    if (!this.token) throw "user is not logged in";
    return this.api.$get<TGetUsersResponse>(GET_USERS());
  }

  changeUser(id: number) {
    return this.api
      .$post<TSelectUserResponse>(CHANGE_USER(), {
        id: String(id)
      })
      .then(async (res) => {
        this.saveTokens(res);
        await this.getUser();
        this.channel?.postMessage({ type: "LOGIN", data: res });
        return this._user;
      })
      .catch(loginErrorHandler);
  }

  // refresh() {
  //   this.refreshing = this.api
  //     .$post<TRefreshTokenResponse>(
  //       REFRESH_TOKEN(),
  //       {
  //         token: this.refreshToken || ""
  //       },
  //       { noRefreshToken: true }
  //     )
  //     .then(async (res) => {
  //       this.saveTokens(res);
  //       this.channel?.postMessage({ type: "LOGOUT", data: res });
  //       this.refreshing = undefined;
  //       await this.getUser();
  //       return this._user as TUser;
  //     })
  //     .catch((e: AxiosError) => {
  //       ServerLogger.error(
  //         "failed to refresh token",
  //         `ERROR: ${e.response?.status}`
  //       );
  //       this.refreshing = undefined;
  //       this.logout();
  //       return Promise.resolve("logout");
  //     });

  //   return this.refreshing as Promise<TUser | "logout">;
  // }

  isExpired() {
    if (!this.token || !this.refreshToken) return;
    const { exp } = jwtDecode<TJWT>(this.token);
    const now = new Date().getTime() / 1000;
    ServerLogger.log(`${now}>${exp}`, "TIME", "magenta");
    return now > exp;
  }

  redirectToDefault() {
    const params =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : this.ctx?.req?.url
          ? new URL(this.ctx?.req?.url).searchParams
          : null;

    if (params?.has("redirect"))
      window.location.href = params.get("redirect") as string;
    else if (this.user?.changePassRequired) {
      redirect("/auth/forget/change", "APPLICATION_URL", this.ctx);
    } else if (this.user?.typeId === UsersTypeEnum.STUDENT) {
      redirect("/dashboard", "APPLICATION_URL", this.ctx);
    } else {
      redirect("/", "GWT_URL", this.ctx);
    }
  }

  get user() {
    return this._user;
  }

  get isLoggedIn() {
    return !!this.token;
  }
}

function createAuth(
  storage: TUtils["storage"],
  options: {
    ctx?: NextPageContext;
    user?: TUser;
  }
) {
  return new Auth(storage, options.ctx, options.user);
}

export default createAuth;
